/**
 * Math, statistical, and conditional-aggregation functions of the
 * Core_Formula_Set (Requirement 4.1).
 *
 * Aggregation follows Excel's split personality, implemented once in
 * `helpers.ts`: a literal argument is coerced (`SUM("3")` is 3), while a cell
 * inside a range contributes only if it is already numeric (`SUM(A1:A3)` ignores
 * text, blanks, and booleans). Errors inside a consumed range propagate.
 *
 * Empty-set behaviour matches Excel too: `SUM`/`COUNT` of nothing is 0,
 * `MIN`/`MAX` of nothing is 0, `AVERAGE` of nothing is `#DIV/0!`, `MEDIAN` of
 * nothing is `#NUM!`, and `STDEV` of fewer than two values is `#DIV/0!`.
 */

import {
  VARIADIC,
  cellsOf,
  err,
  gridOf,
  isError,
  num,
  toScalar,
  type EvalContext,
  type FnSpec,
  type Grid,
  type Scalar,
  type Value,
} from '../values.js';
import {
  alignedSumGrid,
  collectNumbers,
  collectScalars,
  intArg,
  isFailure,
  matchingCells,
  numberArg,
  optIntArg,
  readCriteriaPairs,
  type CriteriaPair,
} from './helpers.js';

type Rounding = 'half' | 'up' | 'down';

/** Rounds `v` to `digits` decimal places under one of the three Excel modes. */
function roundTo(v: number, digits: number, mode: Rounding): number {
  const factor = 10 ** digits;
  const scaled = Math.abs(v) * factor;
  const sign = v < 0 ? -1 : 1;
  const rounded =
    mode === 'half' ? Math.round(scaled) : mode === 'up' ? Math.ceil(scaled) : Math.floor(scaled);
  return (sign * rounded) / factor;
}

/** Numbers of the cells at `hits` within `grid`, skipping non-numeric cells. */
function numbersAt(grid: Grid, hits: readonly { row: number; col: number }[]): number[] {
  const out: number[] = [];
  for (const hit of hits) {
    const cell = grid.get(hit.row, hit.col);
    if (cell.k === 'number') out.push(cell.v);
  }
  return out;
}

/** Shared body of `SUMIF`/`AVERAGEIF`: one criteria pair plus an optional value range. */
function conditionalOneRange(
  args: readonly Value[],
  ctx: EvalContext,
): { values: number[] } | Scalar {
  const pairs = readCriteriaPairs(args.slice(0, 2), 0, ctx);
  if (isFailure(pairs)) return pairs.fail;
  const first = pairs[0] as CriteriaPair;
  const valueGrid = alignedSumGrid(args[2], first.grid, ctx);
  return { values: numbersAt(valueGrid, matchingCells(pairs)) };
}

/** Shared body of `SUMIFS`/`AVERAGEIFS`: value range first, then criteria pairs. */
function conditionalManyRanges(
  args: readonly Value[],
  ctx: EvalContext,
): { values: number[] } | Scalar {
  const pairs = readCriteriaPairs(args, 1, ctx);
  if (isFailure(pairs)) return pairs.fail;
  const first = pairs[0] as CriteriaPair;
  const valueGrid = alignedSumGrid(args[0], first.grid, ctx);
  return { values: numbersAt(valueGrid, matchingCells(pairs)) };
}

function sumOf(values: readonly number[]): number {
  let total = 0;
  for (const v of values) total += v;
  return total;
}

function averageOf(values: readonly number[]): Scalar {
  if (values.length === 0) return err('#DIV/0!');
  return num(sumOf(values) / values.length);
}

/** Sample standard deviation, matching Excel's `STDEV` (divisor `n - 1`). */
function sampleStdev(values: readonly number[]): Scalar {
  if (values.length < 2) return err('#DIV/0!');
  const mean = sumOf(values) / values.length;
  let acc = 0;
  for (const v of values) acc += (v - mean) ** 2;
  return num(Math.sqrt(acc / (values.length - 1)));
}

/** One-number, one-result function over a strict numeric argument. */
function unaryNumeric(name: string, f: (v: number) => Scalar): FnSpec {
  return {
    name,
    min: 1,
    max: 1,
    fn: (args, ctx) => {
      const n = numberArg(args, 0, ctx);
      return isFailure(n) ? n.fail : f(n);
    },
  };
}

/** `ROUND`, `ROUNDUP`, and `ROUNDDOWN` differ only in the rounding mode. */
function roundingFn(name: string, mode: Rounding): FnSpec {
  return {
    name,
    min: 2,
    max: 2,
    fn: (args, ctx) => {
      const value = numberArg(args, 0, ctx);
      if (isFailure(value)) return value.fail;
      const digits = intArg(args, 1, ctx);
      if (isFailure(digits)) return digits.fail;
      return num(roundTo(value, digits, mode));
    },
  };
}

export const MATH_FUNCTIONS: readonly FnSpec[] = [
  {
    name: 'SUM',
    min: 1,
    max: VARIADIC,
    fn: (args, ctx) => {
      const values = collectNumbers(args, ctx);
      return isFailure(values) ? values.fail : num(sumOf(values));
    },
  },
  {
    name: 'SUMIF',
    min: 2,
    max: 3,
    fn: (args, ctx) => {
      const picked = conditionalOneRange(args, ctx);
      return 'values' in picked ? num(sumOf(picked.values)) : picked;
    },
  },
  {
    name: 'SUMIFS',
    min: 3,
    max: VARIADIC,
    fn: (args, ctx) => {
      const picked = conditionalManyRanges(args, ctx);
      return 'values' in picked ? num(sumOf(picked.values)) : picked;
    },
  },
  {
    name: 'SUMPRODUCT',
    min: 1,
    max: VARIADIC,
    fn: (args, ctx) => {
      const grids = args.map((a) => gridOf(a, ctx));
      const first = grids[0];
      if (first === undefined) return err('#VALUE!');
      for (const g of grids) {
        if (g.rows !== first.rows || g.cols !== first.cols) return err('#VALUE!');
      }
      let total = 0;
      for (let r = 0; r < first.rows; r++) {
        for (let c = 0; c < first.cols; c++) {
          let product = 1;
          for (const g of grids) {
            const cell = g.get(r, c);
            if (isError(cell)) return cell;
            product *= cell.k === 'number' ? cell.v : 0;
          }
          total += product;
        }
      }
      return num(total);
    },
  },
  {
    name: 'AVERAGE',
    min: 1,
    max: VARIADIC,
    fn: (args, ctx) => {
      const values = collectNumbers(args, ctx);
      return isFailure(values) ? values.fail : averageOf(values);
    },
  },
  {
    name: 'AVERAGEIF',
    min: 2,
    max: 3,
    fn: (args, ctx) => {
      const picked = conditionalOneRange(args, ctx);
      return 'values' in picked ? averageOf(picked.values) : picked;
    },
  },
  {
    name: 'AVERAGEIFS',
    min: 3,
    max: VARIADIC,
    fn: (args, ctx) => {
      const picked = conditionalManyRanges(args, ctx);
      return 'values' in picked ? averageOf(picked.values) : picked;
    },
  },
  {
    name: 'COUNT',
    min: 1,
    max: VARIADIC,
    errorTolerant: true,
    fn: (args, ctx) => {
      let count = 0;
      for (const value of args) {
        if (value.k === 'ref' || value.k === 'array') {
          for (const cell of cellsOf(value, ctx)) if (cell.k === 'number') count += 1;
        } else if (value.k === 'number') {
          count += 1;
        } else if (value.k === 'text') {
          // A literal numeric string counts, matching Excel.
          const parsed = Number(value.v.trim());
          if (value.v.trim().length > 0 && Number.isFinite(parsed)) count += 1;
        } else if (value.k === 'bool') {
          count += 1;
        }
      }
      return num(count);
    },
  },
  {
    name: 'COUNTA',
    min: 1,
    max: VARIADIC,
    errorTolerant: true,
    fn: (args, ctx) => {
      let count = 0;
      for (const cell of collectScalars(args, ctx)) if (cell.k !== 'empty') count += 1;
      return num(count);
    },
  },
  {
    name: 'COUNTBLANK',
    min: 1,
    max: 1,
    errorTolerant: true,
    fn: (args, ctx) => {
      let count = 0;
      for (const cell of collectScalars(args, ctx)) if (cell.k === 'empty') count += 1;
      return num(count);
    },
  },
  {
    name: 'COUNTIF',
    min: 2,
    max: 2,
    errorTolerant: true,
    fn: (args, ctx) => {
      const pairs = readCriteriaPairs(args, 0, ctx);
      return isFailure(pairs) ? pairs.fail : num(matchingCells(pairs).length);
    },
  },
  {
    name: 'COUNTIFS',
    min: 2,
    max: VARIADIC,
    errorTolerant: true,
    fn: (args, ctx) => {
      const pairs = readCriteriaPairs(args, 0, ctx);
      return isFailure(pairs) ? pairs.fail : num(matchingCells(pairs).length);
    },
  },
  {
    name: 'MIN',
    min: 1,
    max: VARIADIC,
    fn: (args, ctx) => {
      const values = collectNumbers(args, ctx);
      if (isFailure(values)) return values.fail;
      if (values.length === 0) return num(0);
      return num(values.reduce((a, b) => (b < a ? b : a)));
    },
  },
  {
    name: 'MAX',
    min: 1,
    max: VARIADIC,
    fn: (args, ctx) => {
      const values = collectNumbers(args, ctx);
      if (isFailure(values)) return values.fail;
      if (values.length === 0) return num(0);
      return num(values.reduce((a, b) => (b > a ? b : a)));
    },
  },
  {
    name: 'MEDIAN',
    min: 1,
    max: VARIADIC,
    fn: (args, ctx) => {
      const values = collectNumbers(args, ctx);
      if (isFailure(values)) return values.fail;
      if (values.length === 0) return err('#NUM!');
      const sorted = values.slice().sort((a, b) => a - b);
      const mid = sorted.length >> 1;
      if (sorted.length % 2 === 1) return num(sorted[mid] as number);
      return num(((sorted[mid - 1] as number) + (sorted[mid] as number)) / 2);
    },
  },
  {
    name: 'STDEV',
    min: 1,
    max: VARIADIC,
    fn: (args, ctx) => {
      const values = collectNumbers(args, ctx);
      return isFailure(values) ? values.fail : sampleStdev(values);
    },
  },
  roundingFn('ROUND', 'half'),
  roundingFn('ROUNDUP', 'up'),
  roundingFn('ROUNDDOWN', 'down'),
  unaryNumeric('INT', (v) => num(Math.floor(v))),
  unaryNumeric('ABS', (v) => num(Math.abs(v))),
  {
    name: 'MOD',
    min: 2,
    max: 2,
    fn: (args, ctx) => {
      const a = numberArg(args, 0, ctx);
      if (isFailure(a)) return a.fail;
      const b = numberArg(args, 1, ctx);
      if (isFailure(b)) return b.fail;
      if (b === 0) return err('#DIV/0!');
      // Excel's MOD takes the sign of the divisor, unlike JavaScript's `%`.
      return num(a - b * Math.floor(a / b));
    },
  },
  {
    name: 'POWER',
    min: 2,
    max: 2,
    fn: (args, ctx) => {
      const base = numberArg(args, 0, ctx);
      if (isFailure(base)) return base.fail;
      const exponent = numberArg(args, 1, ctx);
      if (isFailure(exponent)) return exponent.fail;
      if (base < 0 && !Number.isInteger(exponent)) return err('#NUM!');
      if (base === 0 && exponent < 0) return err('#DIV/0!');
      const result = base ** exponent;
      return Number.isNaN(result) ? err('#NUM!') : num(result);
    },
  },
  unaryNumeric('SQRT', (v) => (v < 0 ? err('#NUM!') : num(Math.sqrt(v)))),
  {
    name: 'RANK',
    min: 2,
    max: 3,
    fn: (args, ctx) => {
      const target = toScalar(args[0] as Value, ctx);
      if (isError(target)) return target;
      const order = optIntArg(args, 2, ctx, 0);
      if (isFailure(order)) return order.fail;
      const pool = collectNumbers([args[1] as Value], ctx);
      if (isFailure(pool)) return pool.fail;
      const value = target.k === 'number' ? target.v : null;
      if (value === null) return err('#VALUE!');
      if (!pool.includes(value)) return err('#N/A');
      let better = 0;
      for (const v of pool) {
        if (order === 0 ? v > value : v < value) better += 1;
      }
      return num(better + 1);
    },
  },
];
